import React, { useState } from "react";
import {
  Layout,
  Card,
  Tag,
  Typography,
  DatePicker,
  Flex,
  Button,
  Space,
  theme,
} from "antd";
import {
  FileTextOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";

import { Link } from "react-router-dom";
import HeaderApp from "../../shared/components/HeaderApp";
import NavigationBar from "../../shared/components/NavigationBar";
import type { BillingItem, BillingFilter } from "../types";
import { useFetchBills } from "../hooks/userFetchBills";
import Loading from "../../shared/components/Loading";
import BillDetails from "./billDetails";
import { parseJsonBill } from "../utils/parseJsonBill";
import DrawerDetails from "../../shared/components/DrawerDetails";
import FilesSection from "./filesSection";
import { useHybridResponsive } from "../../shared";
import { formatAmount } from "../../../helpers/formatDate";
import EmptyData from "../../shared/components/EmptyData";

const { Text } = Typography;



const BillingList: React.FC = () => {
  const { isMobile } = useHybridResponsive();
  const { token } = theme.useToken();

  const [open, setOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<BillingFilter>("all");
  const [dataShow, setDataShow] = useState<BillingItem | null>(null)

  const filters: { key: BillingFilter; label: string }[] = [
    { key: "all", label: "Todas" },
    { key: "En Proceso", label: "Emitida" },
    { key: "Vigente", label: "Revisada" },
    { key: "Cancelado", label: "Cancelada" },
  ];

  const { data: billsItems, isLoading: isLoadingBills } = useFetchBills();




  const getStatusConfig = (status: BillingItem["status_bill"]) => {
    switch (status) {
      case "En Proceso":
        return {
          color: token.colorSuccess,
          bgColor: "rgba(16, 185, 129, 0.1)",
          label: "Emitida",
          icon: <ClockCircleOutlined style={{ fontSize: "20px" }} />,
        };
      case "Vigente":
        return {
          color: token.colorWarning,
          bgColor: "rgba(249, 115, 22, 0.1)",
          label: "Revisada",
          icon: <FileTextOutlined style={{ fontSize: "20px" }} />,
        };
      case "Cancelado":
        return {
          color: token.colorTextSecondary,
          bgColor: "rgba(107, 114, 128, 0.1)",
          label: "Cancelada",
          icon: <CloseCircleOutlined style={{ fontSize: "20px" }} />,
        };
      default:
        return {
          color: token.colorTextSecondary,
          bgColor: "rgba(107, 114, 128, 0.1)",
          label: "Desconocido",
          icon: <FileTextOutlined style={{ fontSize: "20px" }} />,
        };
    }
  };

  const dataShowDetails = (data: BillingItem) => {
    setOpen(true);
    setDataShow(data);
  }


  if (isLoadingBills) {
    return <Loading />;
  }
  const filteredItems =
    selectedFilter === "all"
      ? billsItems
      : billsItems?.filter((item) => item.status_bill === selectedFilter);
  return (
    <>
      <HeaderApp
        sectionName="Facturación"
        showBack
        backPath="/empresa/dashboard"
      />

      <Layout.Content style={{ padding: "16px", paddingBottom: "140px" }}>
        {/* Date Filter */}
        <div
          className="flex flex-col gap-3 mb-4"
          style={{ marginBottom: "10px" }}
        >
          <Flex justify="center">
            <DatePicker
              picker="month"
              style={{ width: "100%", marginBottom: "10px", marginRight: '10px' }}
            />
            {!isMobile && <>
              <UploadBill />
              <NewBillButton />
            </>}
          </Flex>

          {/* Status Filters */}
          <Flex
            justify="space-between"
            style={{ maxWidth: "400px", margin: "0 auto" }}
          >
            {filters.map((filter) => (
              <Button
                key={filter.key}
                onClick={() => setSelectedFilter(filter.key)}
                style={{
                  padding: "6px 16px",
                  borderRadius: "9999px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  backgroundColor:
                    selectedFilter === filter.key ? token.colorText : token.colorBgContainer,
                  color: selectedFilter === filter.key ? token.colorBgContainer : token.colorTextSecondary,
                  border:
                    selectedFilter === filter.key
                      ? "none"
                      : `1px solid ${token.colorBorder}`,
                  whiteSpace: "nowrap",
                }}
              >
                {filter.label}
              </Button>
            ))}
          </Flex>
        </div>

        {/* Billing List */}
        <div className="flex flex-col gap-3">
          {(!filteredItems || filteredItems.length == 0) ?
            <EmptyData description="No se encontraron facturas" />
            : filteredItems.map((item) => {
              const statusConfig = getStatusConfig(item.status_bill);
              return (
                <Card
                  key={item.id_bill}
                  onClick={() => dataShowDetails(item)}
                  hoverable
                  style={{
                    borderRadius: "12px",
                    cursor: "pointer",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                    border: `1px solid ${token.colorBorderSecondary}`,
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          backgroundColor: statusConfig.bgColor,
                          color: statusConfig.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {statusConfig.icon}
                      </div>
                      <div>
                        <Text
                          strong
                          style={{ fontSize: "14px", color: token.colorText }}
                        >
                          Factura {item.noCertificate}
                        </Text>
                        <br />
                        <Text style={{ fontSize: "12px", color: token.colorTextSecondary }}>
                          {item.receptor_name}
                        </Text>
                      </div>
                    </div>
                    <Space>

                      <div
                        style={{
                          textAlign: "right",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: "4px",
                        }}
                      >
                        <Text
                          strong
                          style={{
                            fontSize: "14px",
                            textDecoration:
                              item.status_bill === "Cancelado"
                                ? "line-through"
                                : "none",
                            color:
                              item.status_bill === "Cancelado"
                                ? token.colorTextPlaceholder
                                : token.colorText,
                          }}
                        >
                          {formatAmount(Number(item.total))}
                        </Text>
                        <Tag
                          style={{
                            borderRadius: "4px",
                            fontSize: "10px",
                            fontWeight: "500",
                            padding: "0 8px",
                            backgroundColor: statusConfig.bgColor,
                            color: statusConfig.color,
                            border: "none",
                          }}
                        >
                          {statusConfig.label}
                        </Tag>
                      </div>

                    </Space>

                  </div>
                </Card>
              );
            })}
        </div>
      </Layout.Content>
      {dataShow &&
        <DrawerDetails
          title="Detalles factura"
          onClose={() => {
            setOpen(false)
          }}
          open={open}
          contentBody={
            <>
              <section>
                {dataShow.uuid_xml && <FilesSection uuid_xml={dataShow.uuid_xml} uuid_pdf={dataShow.uuid_pdf} />}
              </section>
              <BillDetails billJson={parseJsonBill(JSON.parse(dataShow?.json_bill))} />
            </>
          }
        />
      }

      {/* Floating Action Buttons — mobile */}
      {isMobile && (
        <Flex
          gap={12}
          style={{
            position: "fixed",
            bottom: "75px",
            right: "20px",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <UploadBill />
          <NewBillButton />
        </Flex>
      )}
      <NavigationBar activeKey="/facturas" />
    </>

  );
};

export default BillingList;

const UploadBill = () => {
  const { isMobile } = useHybridResponsive();
  const { token } = theme.useToken();

  let desktopStyles = {
    paddingLeft: "14px",
    paddingRight: "14px",
    borderRadius: "10px",
  };

  let mobileStyles = {
    position: 'fixed',
    bottom: '75px',
    right: '20px',
    padding: '30px',
    borderRadius: '15px',
  };

  let styles = {
    fontWeight: "bold",
    backgroundColor: token.colorPrimary,
    color: token.colorTextLightSolid
  }

  if (isMobile) {
    styles = { ...styles, ...mobileStyles }
  } else {
    styles = { ...styles, ...desktopStyles }
  }

  return <div>
    <Link to={"/facturas/subir"}>
      <Button icon={<PlusOutlined style={{ fontSize: '20px' }} />} style={styles}>
        {!isMobile && "Subir factura"}
      </Button>
    </Link>
  </div>;
};

const NewBillButton = () => {
  const { isMobile } = useHybridResponsive();
  const { token } = theme.useToken();

  const desktopStyles: React.CSSProperties = {
    paddingLeft: "14px",
    paddingRight: "14px",
    borderRadius: "10px",
  };

  const mobileStyles: React.CSSProperties = {
    padding: "30px",
    borderRadius: "15px",
  };

  const styles: React.CSSProperties = {
    fontWeight: "bold",
    backgroundColor: token.colorSuccess,
    color: token.colorTextLightSolid,
    border: "none",
    ...(isMobile ? mobileStyles : desktopStyles),
  };

  return (
    <div>
      <Link to="/facturas/nueva">
        <Button
          id="btn-nueva-factura"
          icon={<FileTextOutlined style={{ fontSize: "18px" }} />}
          style={styles}
        >
          {!isMobile && "Nueva factura"}
        </Button>
      </Link>
    </div>
  );
};
